import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useTracks, useCreateLayoutContext, usePinnedTracks, useSpeakingParticipants,
  LayoutContextProvider, FocusLayoutContainer, CarouselLayout, ParticipantTile,
  FocusLayout, ControlBar, Chat, RoomAudioRenderer, ConnectionStateToast, isTrackReference,
} from "@livekit/components-react";
import { Track, RoomEvent } from "livekit-client";
import { BackgroundPicker } from "./BackgroundPicker";

const sameRef = (a, b) =>
  !!a && !!b && a.participant.identity === b.participant.identity && a.source === b.source;

/**
 * Lodge meeting layout: "stage + filmstrip" that follows the active
 * speaker; a shared screen always takes the stage; a manual pin
 * (click on a tile) overrides both. Same behaviour as the mobile app.
 */
export function MeetingStage({ children }) {
  const layoutContext = useCreateLayoutContext();
  const [widget, setWidget] = useState({ showChat: false, unreadMessages: 0 });
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { updateOnlyOn: [RoomEvent.ActiveSpeakersChanged], onlySubscribed: false },
  );
  const speakers = useSpeakingParticipants();
  const pinned = usePinnedTracks(layoutContext)?.[0];
  const lastFocusRef = useRef(null);

  const focus = useMemo(() => {
    const screen = tracks.find((t) => isTrackReference(t) && t.publication.source === Track.Source.ScreenShare);
    if (screen) return screen;
    if (pinned) return tracks.find((t) => sameRef(t, pinned)) || pinned;
    const remoteSpeaker = speakers.find((p) => !p.isLocal);
    if (remoteSpeaker) {
      const cam = tracks.find((t) => t.participant.identity === remoteSpeaker.identity && t.source === Track.Source.Camera);
      if (cam) return cam;
    }
    const last = lastFocusRef.current && tracks.find((t) => sameRef(t, lastFocusRef.current));
    if (last) return last;
    return tracks.find((t) => !t.participant.isLocal) || tracks[0] || null;
  }, [tracks, speakers, pinned]);

  useEffect(() => { if (focus) lastFocusRef.current = focus; }, [focus]);

  const others = tracks.filter((t) => !sameRef(t, focus));

  return (
    <div className="lk-video-conference" data-testid="meeting-stage">
      <LayoutContextProvider value={layoutContext} onWidgetChange={setWidget}>
        <div className="lk-video-conference-inner">
          <div className="lk-focus-layout-wrapper">
            <FocusLayoutContainer>
              <CarouselLayout tracks={others}>
                <ParticipantTile />
              </CarouselLayout>
              {focus ? <FocusLayout trackRef={focus} /> : null}
            </FocusLayoutContainer>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} data-testid="meeting-bottom-bar">
            <ControlBar controls={{ chat: true, screenShare: true, leave: true, settings: false }} />
            <BackgroundPicker />
          </div>
        </div>
        <Chat style={{ display: widget.showChat ? "grid" : "none" }} />
      </LayoutContextProvider>
      <RoomAudioRenderer />
      <ConnectionStateToast />
      {children}
    </div>
  );
}
